import type { Matomo } from "../types/tracking";

const getSveaCookie = () => document.cookie.match(/(^| )CookieConsent_tld=([^;]+)/);

const isValidTarget = (target: EventTarget | null): target is HTMLElement | SVGElement =>
	target instanceof HTMLElement || target instanceof SVGElement;

const getHasTrackingConsent = () => {
	const localConsent = window.localStorage.getItem("trackingConsent");
	if (localConsent) return localConsent === "true";

	const cookie = getSveaCookie();

	return cookie ? cookie[2]?.includes("statistics:true") : false;
};

export const getHasSelectedConsent = () =>
	!!getSveaCookie() || !!window.localStorage.getItem("trackingConsent");

export const setLocalTrackingConsent = (consent: boolean) => {
	window.localStorage.setItem("trackingConsent", String(consent));
};

const trackClick = (matomoInstance?: Matomo) => (event: KeyboardEvent | PointerEvent) => {
	if (!matomoInstance) return;
	if (!getHasTrackingConsent()) return;
	if (!isValidTarget(event.target)) return;

	try {
		const trackers = matomoInstance.getAsyncTrackers?.();

		const buttonEl = event.target.closest("button");
		const menuItem = event.target.closest("[data-menuitem]");
		const anchorEl = event.target.closest("a");

		if (!trackers?.length) return;

		if (menuItem) {
			const eventAction = menuItem.getAttribute("data-tracking-action");
			const eventName = menuItem.getAttribute("data-tracking-name") || undefined;

			if (!eventAction) return;

			trackers.forEach((t) => {
				t.trackEvent("click", eventAction, eventName);
			});
		}

		if (buttonEl) {
			const eventAction = buttonEl.getAttribute("data-tracking-action");
			const eventName = buttonEl.getAttribute("data-tracking-name") || undefined;

			if (!eventAction) return;

			trackers.forEach((t) => {
				t.trackEvent("click", eventAction, eventName);
			});
		}

		if (anchorEl) {
			const href = anchorEl.getAttribute("href");
			if (!href || href.startsWith("/")) return;

			trackers.forEach((t) => {
				t.trackLink(href, "link");
			});
		}
	} catch (e) {
		// oxlint-disable-next-line no-console
		console.error(e);
	}
};

const trackPageView = (matomoInstance?: Matomo) => (pathName: string) => {
	if (!matomoInstance) return;
	if (!getHasTrackingConsent()) return;

	try {
		const trackers = matomoInstance.getAsyncTrackers?.();
		trackers?.forEach((t) => {
			t.setCustomUrl(pathName);
			t.trackPageView();
		});
	} catch (e) {
		// oxlint-disable-next-line no-console
		console.error(e);
	}
};

const setCustomDimension = (matomoInstance?: Matomo) => (id: number, value: string) => {
	if (!matomoInstance) return;
	if (!getHasTrackingConsent()) return;

	try {
		const trackers = matomoInstance.getAsyncTrackers?.();

		trackers?.forEach((t) => {
			t.setCustomDimension(id, value);
		});
	} catch (e) {
		// oxlint-disable-next-line no-console
		console.error(e);
	}
};

const trackAllContentImpressions = (matomoInstance?: Matomo) => () => {
	if (!matomoInstance) return;
	if (!getHasTrackingConsent()) return;

	try {
		const trackers = matomoInstance.getAsyncTrackers?.();

		trackers?.forEach((t) => {
			t.trackAllContentImpressions();
		});
	} catch (e) {
		// oxlint-disable-next-line no-console
		console.error(e);
	}
};

export const getTrackingFns = (matomoInstance?: Matomo) => ({
	trackClick: trackClick(matomoInstance),
	trackPageView: trackPageView(matomoInstance),
	setCustomDimension: setCustomDimension(matomoInstance),
	trackAllContentImpressions: trackAllContentImpressions(matomoInstance),
});
